import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, User, Phone, Calendar, 
  Send, Clock, FileText, Pill, Filter, X, CheckCircle, 
  Edit, Save, Trash2, AlertTriangle, 
  PhoneMissed, Voicemail, RefreshCw, Printer, Mail, HelpCircle, CheckCircle2 
} from 'lucide-react';

const TrackerDetails = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  
  const [info, setInfo] = useState(null); 
  const [patientPrescriptions, setPatientPrescriptions] = useState([]); 
  const [remarks, setRemarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRx, setSelectedRx] = useState(null);

  // Edit Prescription Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Main Form State
  const [customStatus, setCustomStatus] = useState('');
  
  // Delete Remark State
  const [showDeleteRemarkModal, setShowDeleteRemarkModal] = useState(false);
  const [remarkToDelete, setRemarkToDelete] = useState(null);

  // ✅ NEW: Edit Log/History State
  const [showEditLogModal, setShowEditLogModal] = useState(false);
  const [editLogData, setEditLogData] = useState({ id: null, title: '', status: '', remark: '' });

  // 🎨 STATUS CONFIGURATION
  const STATUS_OPTIONS = [
    { value: 'NO ANSWER', label: 'No Answer', color: 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100', icon: PhoneMissed },
    { value: 'VOICEMAIL', label: 'Voicemail', color: 'bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100', icon: Voicemail },
    { value: 'FOLLOW UP', label: 'Follow Up', color: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100', icon: Clock },
    { value: 'CALLBACK', label: 'Callback', color: 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100', icon: Phone },
    { value: 'SEND EMAIL', label: 'Email Sent', color: 'bg-indigo-50 text-indigo-600 border-indigo-200 hover:bg-indigo-100', icon: Mail },
    { value: 'SEND FAX', label: 'Fax Sent', color: 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100', icon: Printer },
    { value: 'PROCESS', label: 'Processing', color: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100', icon: RefreshCw },
    { value: 'COMPLETED', label: 'Completed', color: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100', icon: CheckCircle2 },
    { value: 'OTHERS', label: 'Others', color: 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100', icon: HelpCircle },
  ];

  const getAgentName = () => {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) return 'Unknown Agent';
      const u = JSON.parse(stored);
      return u.full_name || u.name || u.username || 'Agent';
    } catch (err) { return 'Agent'; }
  };

  const agentName = getAgentName(); 

  const getCurrentDateTime = () => {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset()); 
      return now.toISOString().slice(0, 16);
  };

  const [formData, setFormData] = useState({ 
      title: '', 
      remark: '', 
      status: 'NO ANSWER', 
      log_date: getCurrentDateTime() 
  });

  const API_URL = 'https://unifyrxcrm.com/AG-BACKEND/index.php';

  useEffect(() => {
    fetchPageData();
  }, [id]);

  const fetchPageData = async () => {
    try {
      const res = await axios.get(`${API_URL}?action=get_tracker_detail&id=${id}&_t=${Date.now()}`);
      const data = res.data;
      setInfo(data);
      
      if (data) {
          if (data.rx_no) setSelectedRx(data.rx_no);
          if (data.prescriber_name) fetchRemarks(data.prescriber_name);
          if (data.patient_id) fetchAllPatientRx(data.patient_id);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const fetchAllPatientRx = async (patientId) => {
      try {
          const res = await axios.get(`${API_URL}?action=get_patient_details&id=${patientId}&_t=${Date.now()}`);
          if(res.data && Array.isArray(res.data.prescriptions)) {
              setPatientPrescriptions(res.data.prescriptions);
          }
      } catch (err) { console.error(err); }
  };

  const fetchRemarks = async (doctorName) => {
    try {
      const encodedName = encodeURIComponent(doctorName);
      const res = await axios.get(`${API_URL}?action=get_doctor_remarks&name=${encodedName}&_t=${Date.now()}`);
      if(Array.isArray(res.data)) setRemarks(res.data);
    } catch (err) { console.error(err); }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStatusSelect = (statusValue) => {
    setFormData({ ...formData, status: statusValue });
  };

  // --- EDIT PRESCRIPTION MODAL HANDLERS ---
  const handleEditRx = (rx, e) => {
    e.stopPropagation(); 
    setEditData({ ...rx, ndc: rx.ndc || '' }); 
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const saveEditedRx = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const response = await axios.post(`${API_URL}?action=update_prescription_modal`, {
        id: editData.id, 
        rx_no: editData.rx_no,
        drug_name: editData.drug_name,
        ndc: editData.ndc,
        quantity: editData.quantity,
        days_supply: editData.days_supply,
        expiration_date: editData.expiration_date,
        status: editData.status
      });

      if (response.data.success) {
          setPatientPrescriptions([]); 
          await fetchAllPatientRx(info.patient_id);
          setShowEditModal(false);
      } else {
          alert("Update failed: " + (response.data.error || "Unknown Error"));
      }
      setSavingEdit(false);
    } catch (err) {
      console.error(err);
      alert('Network Error - Check Console');
      setSavingEdit(false);
    }
  };

  // --- DELETE REMARK HANDLERS ---
  const confirmDeleteRemark = (remark) => {
      setRemarkToDelete(remark);
      setShowDeleteRemarkModal(true);
  };

  const executeDeleteRemark = async () => {
      if (!remarkToDelete) return;
      try {
          await axios.post(`${API_URL}?action=delete_remark`, { id: remarkToDelete.remarksid });
          setShowDeleteRemarkModal(false);
          setRemarkToDelete(null);
          fetchRemarks(info.prescriber_name);
      } catch (err) {
          console.error(err);
          alert("Failed to delete remark.");
      }
  };

  // --- ADD REMARK HANDLER ---
  const handleAddRemark = async (e) => {
    e.preventDefault();
    if (!formData.remark.trim()) {
        alert("Please enter a remark.");
        return;
    }
    
    let finalStatus = formData.status;
    if (formData.status === 'OTHERS') {
        if (!customStatus.trim()) {
            alert("Please specify the details for 'Others'.");
            return;
        }
        finalStatus = `OTHERS: ${customStatus}`;
    }

    setSaving(true);

    try {
      const remarkRes = await axios.post(`${API_URL}?action=add_remark`, {
        prescriber_name: info.prescriber_name,
        rx_no: selectedRx, 
        title: formData.title, 
        remarks: formData.remark, 
        status: finalStatus, 
        added_by: agentName,
        log_date: formData.log_date 
      });

      if (!remarkRes.data.success) {
          throw new Error("Failed to save remark");
      }

      if (selectedRx) {
          await axios.post(`${API_URL}?action=update_prescription_status`, {
              rx_no: selectedRx, 
              status: finalStatus
          });
          if (info.patient_id) {
              await fetchAllPatientRx(info.patient_id);
          }
      }
      
      setFormData(prev => ({ 
          ...prev, 
          remark: '', 
          title: '', 
          status: 'NO ANSWER',
          log_date: getCurrentDateTime()
      }));
      setCustomStatus(''); 
      fetchRemarks(info.prescriber_name);
      
    } catch (err) { 
        console.error("Error Saving:", err);
        alert("Error saving record."); 
    } finally {
        setSaving(false); 
    }
  };

  // ✅ NEW: EDIT HISTORY LOG HANDLERS
  const handleEditLogClick = (e, log) => {
      e.stopPropagation();
      setEditLogData({
          id: log.remarksid,
          title: log.title || '',
          status: log.status || 'NO ANSWER', // Default to avoid controlled/uncontrolled warning
          remark: log.remarks || ''
      });
      setShowEditLogModal(true);
  };

  const handleSaveEditedLog = async (e) => {
      e.preventDefault();
      try {
          await axios.post(`${API_URL}?action=update_remark`, editLogData);
          setShowEditLogModal(false);
          fetchRemarks(info.prescriber_name); // Refresh list
      } catch (error) {
          console.error(error);
          alert("Error updating log.");
      }
  };

  const filteredRemarks = selectedRx 
    ? remarks.filter(r => r.rx_no === selectedRx) 
    : remarks;

  const formatCardDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-PH', { month: 'short', day: '2-digit', year: 'numeric' }) + ' ' + date.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '-';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const getStatusConfig = (statusStr) => {
    if(!statusStr) return STATUS_OPTIONS[0];
    const cleanStatus = statusStr.split(':')[0].trim();
    return STATUS_OPTIONS.find(s => s.value === cleanStatus) || 
           { color: 'bg-gray-100 text-gray-600', icon: FileText, label: statusStr };
  };

  if (loading) return <div className="flex h-screen justify-center items-center text-gray-400">Loading...</div>;
  if (!info || info.error) return <div className="flex h-screen justify-center items-center text-red-400">Record not found.</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex flex-col font-sans relative">
      
      {/* Header */}
      <div className="flex items-center mb-5">
        <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-teal-600 transition mr-4">
          <ArrowLeft size={22} />
        </button>
        <div>
            <h1 className="text-xl font-bold text-gray-800">Tracker Details</h1>
            <p className="text-xs text-gray-500">Call Logs & Remarks</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch h-[calc(100vh-120px)]">

        {/* --- CARD 1: CONTEXT INFO --- */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 flex flex-col h-full overflow-hidden">
            <div className="mb-4 border-b border-gray-100 pb-4">
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2 mb-2"><User size={14}/> Patient</h2>
                <p className="text-lg font-bold text-gray-800 leading-tight">{info.patient_name}</p>
                <div className="flex gap-4 mt-2"><span className="text-xs text-gray-500 flex items-center gap-1"><Phone size={12}/> {info.patient_phone}</span><span className="text-xs text-gray-500 flex items-center gap-1"><Calendar size={12}/> {formatDate(info.dob)}</span></div>
            </div>

            <div className="mb-4 border-b border-gray-100 pb-4">
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2 mb-2"><User size={14}/> Prescriber</h2>
                <p className="text-base font-bold text-teal-700">{info.prescriber_name}</p>
                <span className="text-xs text-gray-500 flex items-center gap-1 mt-1"><Phone size={12}/> {info.prescriber_phone}</span>
            </div>

            <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs font-bold text-teal-600 uppercase tracking-wider flex items-center gap-2">
                    <Pill size={14}/> Active Prescriptions
                </h2>
                {selectedRx && (
                    <button onClick={() => setSelectedRx(null)} className="text-[10px] text-gray-400 hover:text-red-500 flex items-center gap-1">
                        <X size={10}/> Clear Selection
                    </button>
                )}
            </div>
            
            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-3">
                {patientPrescriptions.map((rx, index) => (
                    <div 
                        key={index} 
                        onClick={() => setSelectedRx(rx.rx_no)}
                        className={`p-3 rounded border cursor-pointer transition relative group
                            ${selectedRx === rx.rx_no 
                                ? 'bg-teal-50 border-teal-400 ring-1 ring-teal-300 shadow-sm' 
                                : 'bg-gray-50 border-gray-100 hover:border-teal-200 hover:bg-white'}`}
                    >
                         {selectedRx === rx.rx_no && <div className="absolute right-2 top-2 text-teal-600"><CheckCircle size={14}/></div>}
                        
                        <div className="flex justify-between items-start mb-1 pr-6">
                            <span className={`text-[10px] font-bold uppercase ${selectedRx === rx.rx_no ? 'text-teal-700' : 'text-gray-500'}`}>RX #{rx.rx_no}</span>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] bg-white px-1.5 rounded border text-gray-600">{rx.status}</span>
                                <button onClick={(e) => handleEditRx(rx, e)} className="p-1 text-gray-400 hover:text-teal-600 hover:bg-teal-100 rounded transition z-10"><Edit size={12} /></button>
                            </div>
                        </div>

                        <p className="text-sm font-bold text-gray-800">{rx.drug_name}</p>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                            <div className="text-center"><span className="block text-[9px] text-gray-400">Qty</span><span className="text-xs font-medium">{rx.quantity}</span></div>
                            <div className="text-center"><span className="block text-[9px] text-gray-400">Days</span><span className="text-xs font-medium">{rx.days_supply}</span></div>
                            <div className="text-center"><span className="block text-[9px] text-gray-400">Exp</span><span className="text-xs font-bold text-red-500">{formatDate(rx.expiration_date)}</span></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        {/* --- CARD 2: ACTION FORM --- */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 flex flex-col h-full">
            <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-2">
                <div className="flex items-center gap-2 text-gray-400">
                    <Calendar size={16}/>
                    <span className="text-xs font-bold uppercase tracking-wider">Log Date</span>
                </div>
                <input 
                    type="datetime-local" 
                    name="log_date"
                    value={formData.log_date} 
                    onChange={handleInputChange} 
                    className="text-sm font-bold text-teal-700 border-none focus:ring-0 cursor-pointer bg-transparent text-right"
                />
            </div>

            <form onSubmit={handleAddRemark} className="flex flex-col gap-4 justify-start h-full">
                <div>
                    <label className="block text-[10px] font-bold text-gray-500 mb-1">Title</label>
                    <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-1 focus:ring-teal-500 outline-none" placeholder="Enter title..."/>
                </div>
                
                <div>
                    <label className="block text-[10px] font-bold text-gray-500 mb-2">Call Outcome</label>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                        {STATUS_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => handleStatusSelect(opt.value)}
                                className={`
                                    flex flex-col items-center justify-center p-2 rounded-md border text-xs font-medium transition-all duration-200
                                    ${formData.status === opt.value 
                                        ? `${opt.color} ring-1 ring-offset-1 ring-gray-300 font-bold shadow-sm` 
                                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                    }
                                `}
                            >
                                <opt.icon size={16} className="mb-1 opacity-80" />
                                <span className="text-[9px] uppercase">{opt.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className={`transition-all duration-300 ease-in-out overflow-hidden ${formData.status === 'OTHERS' ? 'max-h-20 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                        <input 
                            type="text" 
                            value={customStatus} 
                            onChange={(e) => setCustomStatus(e.target.value)} 
                            className="w-full border border-teal-300 bg-teal-50 rounded p-2 text-sm focus:ring-1 focus:ring-teal-500 outline-none" 
                            placeholder="Please specify outcome details..."
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-[10px] font-bold text-gray-500 mb-1">Agent Remarks</label>
                    <textarea 
                        name="remark" 
                        value={formData.remark} 
                        onChange={handleInputChange} 
                        className="w-full h-24 border border-gray-300 rounded p-3 text-sm focus:ring-1 focus:ring-teal-500 outline-none resize-y" 
                        placeholder="Type call details here..."
                    ></textarea>
                </div>
                
                <button type="submit" disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-2.5 rounded transition flex justify-center items-center gap-2 text-sm shadow-sm mt-auto">{saving ? 'Saving...' : <><Send size={14} /> Save Record</>}</button>
            </form>
        </div>

        {/* --- CARD 3: HISTORY --- */}
        <div className="flex flex-col h-full">
            <div className="flex justify-between items-center mb-3">
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2"><Clock size={14}/> History</h2>
                <div className="flex gap-2">
                    {selectedRx && (
                        <span className="bg-teal-100 text-teal-700 text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                            <Filter size={10}/> RX: {selectedRx}
                        </span>
                    )}
                    <span className="bg-gray-200 text-gray-600 text-[10px] px-2 py-0.5 rounded-full">{filteredRemarks.length}</span>
                </div>
            </div>
            
            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-3 pb-2">
                {filteredRemarks.length === 0 ? (
                    <div className="h-full flex flex-col justify-center items-center text-gray-400 text-sm italic border border-dashed border-gray-300 rounded-lg">
                        <p>{selectedRx ? 'No logs for this RX.' : 'No history available.'}</p>
                    </div>
                ) : (
                    filteredRemarks.map((rem) => {
                        const statusConfig = getStatusConfig(rem.status);
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                            <div key={rem.remarksid} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition relative group">
                                
                                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                    {/* ✅ NEW: Edit Log Button */}
                                    <button 
                                        onClick={(e) => handleEditLogClick(e, rem)} 
                                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                        title="Edit Log"
                                    >
                                        <Edit size={14} />
                                    </button>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); confirmDeleteRemark(rem); }} 
                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                        title="Delete Log"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>

                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className={`p-1.5 rounded-full ${statusConfig.color.split(' ')[0]}`}>
                                            <StatusIcon size={14} className={statusConfig.color.split(' ')[1]} />
                                        </div>
                                        <h3 className="font-bold text-gray-800 text-sm uppercase">{rem.title || 'Call Log'}</h3>
                                    </div>
                                    <span className="text-[10px] text-gray-400 text-right leading-tight min-w-[80px] pr-5">{formatCardDate(rem.timestamp)}</span>
                                </div>

                                <div className="flex items-center gap-2 mb-2 pl-8">
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase ${statusConfig.color}`}>
                                        {rem.status.includes('OTHERS') ? rem.status : statusConfig.label}
                                    </span>
                                </div>
                                
                                <p className="text-sm text-gray-600 leading-relaxed mb-3 pl-8 whitespace-pre-wrap">{rem.remarks}</p>
                                <p className="text-[10px] text-gray-400 pl-8 flex items-center gap-1">
                                    <User size={10} /> {rem.added_by || 'System'}
                                </p>
                            </div>
                        );
                    })
                )}
            </div>
        </div>

      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteRemarkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-80 p-6 relative text-center animate-fade-in-up">
                <div className="mx-auto flex items-center justify-center h-10 w-10 rounded-full bg-red-100 mb-4">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Delete Log?</h3>
                <p className="text-xs text-gray-500 mt-2 mb-6">
                    This action cannot be undone. Are you sure you want to delete this call log?
                </p>
                <div className="flex justify-center gap-3">
                    <button onClick={() => setShowDeleteRemarkModal(false)} className="flex-1 bg-white text-gray-700 border border-gray-300 py-2 rounded font-medium text-sm hover:bg-gray-50">Cancel</button>
                    <button onClick={executeDeleteRemark} className="flex-1 bg-red-600 text-white py-2 rounded font-medium text-sm hover:bg-red-700">Delete</button>
                </div>
            </div>
        </div>
      )}

      {/* ✅ NEW: EDIT LOG MODAL */}
      {showEditLogModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md animate-fade-in-up">
                <div className="bg-blue-600 px-5 py-3 flex justify-between items-center rounded-t-lg">
                    <h3 className="text-white font-bold text-sm flex items-center gap-2"><Edit size={16}/> Edit Call Log</h3>
                    <button onClick={() => setShowEditLogModal(false)} className="text-blue-100 hover:text-white"><X size={18}/></button>
                </div>
                <form onSubmit={handleSaveEditedLog} className="p-6 flex flex-col gap-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Title</label>
                        <input 
                            type="text" 
                            className="w-full border rounded p-2 text-sm focus:ring-1 focus:ring-blue-500 outline-none" 
                            value={editLogData.title} 
                            onChange={e => setEditLogData({...editLogData, title: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Outcome</label>
                        <select 
                            className="w-full border rounded p-2 text-sm bg-white"
                            value={editLogData.status}
                            onChange={e => setEditLogData({...editLogData, status: e.target.value})}
                        >
                            {STATUS_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Remarks</label>
                        <textarea 
                            className="w-full h-32 border rounded p-2 text-sm focus:ring-1 focus:ring-blue-500 outline-none resize-none"
                            value={editLogData.remark}
                            onChange={e => setEditLogData({...editLogData, remark: e.target.value})}
                        ></textarea>
                    </div>
                    <div className="flex gap-3 mt-2">
                        <button type="button" onClick={() => setShowEditLogModal(false)} className="flex-1 bg-gray-100 text-gray-600 py-2 rounded text-sm font-bold hover:bg-gray-200">Cancel</button>
                        <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded text-sm font-bold hover:bg-blue-700">Save Changes</button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {/* Edit Rx Modal */}
      {showEditModal && editData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-fade-in-up">
                <div className="bg-teal-600 px-5 py-4 flex justify-between items-center">
                    <h3 className="text-white font-bold text-sm flex items-center gap-2"><Edit size={16}/> Edit Prescription: {editData.rx_no}</h3>
                    <button onClick={() => setShowEditModal(false)} className="text-teal-100 hover:text-white"><X size={18}/></button>
                </div>
                
                <form onSubmit={saveEditedRx} className="p-6 flex flex-col gap-4">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Drug Name</label>
                        <input type="text" name="drug_name" value={editData.drug_name} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 text-sm mt-1 focus:ring-1 focus:ring-teal-500 outline-none" />
                    </div>

                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">NDC</label>
                        <input 
                            type="text" 
                            name="ndc" 
                            value={editData.ndc} 
                            onChange={handleEditChange} 
                            className="w-full border border-gray-300 rounded p-2 text-sm mt-1 focus:ring-1 focus:ring-teal-500 outline-none" 
                            placeholder="00000-0000-00"
                        />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Quantity</label>
                            <input type="number" name="quantity" value={editData.quantity} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 text-sm mt-1 outline-none"/>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Days Supply</label>
                            <input type="number" name="days_supply" value={editData.days_supply} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 text-sm mt-1 outline-none"/>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Expiration</label>
                            <input type="date" name="expiration_date" value={editData.expiration_date} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 text-sm mt-1 outline-none"/>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Status</label>
                            <select name="status" value={editData.status} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 text-sm mt-1 outline-none bg-white">
                                {STATUS_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mt-4 flex gap-3">
                        <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 bg-gray-100 text-gray-600 py-2 rounded font-bold text-sm hover:bg-gray-200 transition">Cancel</button>
                        <button type="submit" disabled={savingEdit} className="flex-1 bg-teal-600 text-white py-2 rounded font-bold text-sm hover:bg-teal-700 transition flex justify-center items-center gap-2">
                           {savingEdit ? 'Saving...' : <><Save size={14}/> Save Changes</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}

    </div>
  );
};

export default TrackerDetails;